import { clsx } from 'clsx';
import type { CSSProperties, ReactNode } from 'react';
import { useMemo, useState } from 'react';

export interface ListableInfoProps {
    /**
     * Label of the info
     */
    label: string | ReactNode;
    /**
     * Value of the info
     */
    value: string | ReactNode;
    /**
     * Available sizes to create consistency between different content in the same list
     */
    size?: 'xs' | 'sm' | 'md';
    /**
     * Orientation of the info
     */
    orientation?: 'horizontal' | 'vertical';
    /**
     * Provide an additional class name to the component
     */
    className?: string;
    /**
     * Overwrite style properties
     */
    style?: CSSProperties;
    /**
     * Overwrite style properties for the "label" element
     */
    labelStyle?: CSSProperties;
    /**
     * Overwrite style properties for the "value" element
     */
    valueStyle?: CSSProperties;
    /*
     * Additional class name for the "label" element
     */
    labelClassName?: string;
    /*
     * Whether to allow text wrapping for long text values
     */
    wrap?: boolean;
    /*
     * Whether to hide a fallback dash when value is empty
     */
    disableEmptyFallback?: boolean;
    /*
     * Test identifier. When omitted, a stable `detail-<kebab-label>` id is
     * derived from the (string) label so info-card rows are targetable.
     */
    'data-testid'?: string;
}

function toDetailTestId(label: string): string {
    const kebab = label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

    return kebab ? `detail-${kebab}` : '';
}

export function ListableInfo({
    disableEmptyFallback,
    label,
    value,
    size = 'sm',
    orientation = 'horizontal',
    style,
    className,
    labelStyle,
    valueStyle,
    wrap,
    labelClassName: labelClassNameProps,
    'data-testid': dataTestId,
}: ListableInfoProps): ReactNode {
    const [showMore, setShowMore] = useState(false);
    const isMore = useMemo(
        () => typeof value === 'string' && value.length > 400,
        [value],
    );

    const classes = clsx(
        'listable-info',
        {
            [`listable-info--${size}`]: size,
            [`listable-info--${orientation}`]: orientation,
            [`listable-info--with-more`]: isMore,
            [`listable-info--wrap`]: wrap,
        },
        className,
    );

    const labelIsString = typeof label === 'string';

    const testId =
        dataTestId ??
        (labelIsString
            ? toDetailTestId(label as string) || undefined
            : undefined);

    if (labelIsString) {
        const endsWithColon = /(:|\*:$)/g.test(label as string);
        if (!endsWithColon) {
            label = `${label}:`;
        }
    }

    const valueContent = useMemo(() => {
        if (!isMore || showMore || !value) {
            if (!value) {
                return disableEmptyFallback ? value : '-';
            }

            return value;
        }
        return (
            <span>
                {value.toString().slice(0, 400)}...{' '}
                <a
                    className="listable-info__more-button"
                    onClick={() => {
                        setShowMore(true);
                    }}
                >
                    read more
                </a>
            </span>
        );
    }, [isMore, showMore, value, disableEmptyFallback]);
    const labelClassName = clsx('listable-info__label', labelClassNameProps);

    return (
        <div className={classes} style={style} data-testid={testId}>
            <span className={labelClassName} style={labelStyle}>
                {label}
            </span>
            <span
                className="listable-info-value flex align-items-center"
                style={valueStyle}
            >
                {valueContent}
            </span>
        </div>
    );
}
